import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { collectionData, DocumentReference, orderBy, query, Timestamp, where } from '@angular/fire/firestore';
import { FormGroup } from '@angular/forms';
import { IInventoryCategory } from '@inventory/models/inventory';
import { IInventoryCategoryCreateForm, IInventoryCategoryEditForm } from '@inventory/models/inventory-forms';
import { FireStoreCollection } from '@shared/models/firestore';
import { FirestoreService } from '@shared/services/firestore/firestore.service';

@Injectable({
	providedIn: 'root',
})
export class InventoryCategoriesService extends FirestoreService<IInventoryCategory> {
	constructor() {
		super(FireStoreCollection.INVENTORY_CATEGORIES);
	}

	getInventoryCategoriesByOrganisation(
		organisationRef: DocumentReference,
		showArchived: boolean = false,
	): Observable<Array<IInventoryCategory>> {
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));
		inventoryQuery = query(inventoryQuery, where('archived', '==', showArchived));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryCategory>(inventoryQuery, { idField: 'id' });
	}

	getInventoryCategoriesByOrganisationAll(organisationRef: DocumentReference): Observable<Array<IInventoryCategory>> {
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryCategory>(inventoryQuery, { idField: 'id' });
	}

	async createInventoryCategory(
		organisationRef: DocumentReference,
		inventoryCategory: FormGroup<IInventoryCategoryCreateForm>,
	): Promise<DocumentReference<IInventoryCategory>> {
		const nameValue = inventoryCategory.controls.name.value as string;
		const descriptionValue = inventoryCategory.controls.description.value as string | null;

		return this.create({
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			archived: false,
			organisation: organisationRef,
			createdDate: Timestamp.now(),
			updatedDate: Timestamp.now(),
		});
	}

	async updateInventoryCategory(inventoryCategoryId: string, inventoryCategory: FormGroup<IInventoryCategoryEditForm>): Promise<void> {
		const nameValue = inventoryCategory.controls.name.value as string;
		const descriptionValue = inventoryCategory.controls.description.value as string | null;
		const archivedValue = inventoryCategory.controls.archived.value as boolean;

		return this.update(inventoryCategoryId, {
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			archived: archivedValue ?? false,
			updatedDate: Timestamp.now(),
		});
	}

	async archiveInventoryCategory(inventoryCategoryId: string): Promise<void> {
		return this.update(inventoryCategoryId, {
			archived: true,
			updatedDate: Timestamp.now(),
		});
	}

	async unArchiveInventoryCategory(inventoryCategoryId: string): Promise<void> {
		return this.update(inventoryCategoryId, {
			archived: false,
			updatedDate: Timestamp.now(),
		});
	}
}
